/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, {
	type PropsWithChildren,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { CONFIGURABLE_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import {
	NavItemsContext,
	PeekMenuIdContext,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import type { ConfigurableMenuId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';

/**
 * Routes where sidebar peek is disabled.
 * On these routes, the selected path is used for highlighting only, not for auto-expanding.
 * Mirrors the equivalent guard in
 * `@jira/nav-platform-integration__sidebar` (`usePeekMenuId`).
 */
const PEEK_DISABLED_ROUTES = new Set<RouteName>(['for-you']);

// Eventually this will be all configurable menu ids and can be removed at that point.
// TODO: Adam, I think we've reached that point.
const CONFIGURABLE_MENU_IDS_USING_SIDEBAR_CONTEXT: ConfigurableMenuId[] = [
	CONFIGURABLE_MENU_ID.ASSETS,
	CONFIGURABLE_MENU_ID.APPS,
	CONFIGURABLE_MENU_ID.DASHBOARDS,
	CONFIGURABLE_MENU_ID.FILTERS,
	CONFIGURABLE_MENU_ID.OPERATIONS,
	CONFIGURABLE_MENU_ID.PLANS,
	CONFIGURABLE_MENU_ID.PROJECTS,
];

type PeekMenuIdContextProviderProps = PropsWithChildren<{}>;

export const PeekMenuIdContextProvider = ({
	children,
}: PeekMenuIdContextProviderProps): React.JSX.Element => {
	const { isSelectedPath } = useSidebarNav4();
	const { isControlledNav, isControlledPath } = useSidebarNav4Controller();
	const { moreMenuIds } = useContext(NavItemsContext);
	const currentRoute = useCurrentRoute();
	const currentRouteName = currentRoute.name;

	const [peekMenuId, setPeekMenuId] = useState(() =>
		getAutoPeekMenuId(
			isSelectedPath,
			isControlledNav,
			isControlledPath,
			moreMenuIds,
			currentRouteName,
		),
	);

	// This effect automatically overrides the manual value of peek menu id (set when clicking in the more menu item).
	// This effect is only triggered when controlled, selected, or more menu ids change.
	// Use layout effect so peek menu id is set before render.
	// An alternative to any effect would be to set peek menu id when setting selected path.
	// However, that may require elevating this context.
	useLayoutEffect(
		() =>
			setPeekMenuId((currentPeekMenuId) =>
				getAutoPeekMenuId(
					isSelectedPath,
					isControlledNav,
					isControlledPath,
					moreMenuIds,
					currentRouteName,
					currentPeekMenuId,
				),
			),
		[isControlledNav, isControlledPath, isSelectedPath, moreMenuIds, currentRouteName],
	);

	const peekMenuIdContext = useMemo(() => ({ peekMenuId, setPeekMenuId }), [peekMenuId]);

	return (
		<PeekMenuIdContext.Provider value={peekMenuIdContext}>{children}</PeekMenuIdContext.Provider>
	);
};

const getAutoPeekMenuId = (
	isSelectedPath: (menuId: string) => boolean,
	isControlledNav: boolean,
	isControlledPath: (menuId: string) => boolean,
	moreMenuIds: ConfigurableMenuId[],
	currentRouteName: RouteName,
	peekMenuId?: ConfigurableMenuId | undefined,
): ConfigurableMenuId | undefined => {
	// When controlled...
	if (isControlledNav) {
		return moreMenuIds.find(isControlledPath);
	}

	// Otherwise selected...
	const autoPeekMenuId = moreMenuIds.find(isSelectedPath);

	if (
		PEEK_DISABLED_ROUTES.has(currentRouteName) &&
		expVal('blu-9028-disable-auto-peek-non-gsn', 'isEnabled', false)
	) {
		return undefined;
	}

	// Once all configurable menu ids are using sidebar context we can just return `autoPeekMenuId`.
	if (autoPeekMenuId) {
		return autoPeekMenuId;
	}

	// Unpeek if peek menu id is no longer selected.
	// Only do this for menu ids using the sidebar context for now.
	// Legacy menu ids set peek menu id manually.
	if (peekMenuId && CONFIGURABLE_MENU_IDS_USING_SIDEBAR_CONTEXT.includes(peekMenuId)) {
		return undefined;
	}

	// Otherwise unchanged.
	return peekMenuId;
};
